import { useCallback } from 'react';

import {
  FetchLifestylesCacheKey,
  FetchLifestylesFilters,
  FetchLifestylesGqlQuery,
  FetchLifestylesLimit,
  FetchLifestylesSort,
  LifestyleNanoID
} from '../../lifestylesTypes';
import {
  FetchCacheItemSources,
  FetchCacheItemsSources,
  FetchCacheItemsTrackTotalHits,
  FetchItemCacheKey,
  FetchItemGqlQuery,
  FetchItemsGqlQuery,
  UUID
} from '../../../../types';
import { ReadInfiniteIndexQueryBaseNodeType } from '../../../common/reactQuery/hooks/useReadInfiniteIndexQuery';

import { useCacheInfiniteIndexQuery } from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  INITIAL_LIFESTYLES_FILTERS,
  INITIAL_LIFESTYLES_LIMIT,
  INITIAL_LIFESTYLES_SORT,
  LifestylesPermissions
} from '../../lifestylesConstants';

interface CacheLifestylesWithPrefetchItemOptions {
  fetchItemCacheKey: FetchItemCacheKey;
  fetchItemQuery: FetchItemsGqlQuery;
  fetchCacheItemQuery?: FetchItemGqlQuery;
  fetchCacheItemSource?: FetchCacheItemSources;
}

interface CacheLifestylesWithoutPrefetchItemOptions {
  fetchItemCacheKey?: never;
  fetchItemQuery?: never;
  fetchCacheItemQuery?: never;
  fetchCacheItemSource?: never;
}

interface CacheLifestylesDefaultOptions {
  cacheKey: FetchLifestylesCacheKey;
  query: FetchLifestylesGqlQuery;
  cacheQuery?: FetchLifestylesGqlQuery;
  defaultApi?: 'api' | 'read_api' | 'fin_api' | 'cache_api';
  initialFilters?: FetchLifestylesFilters;
  initialSort?: FetchLifestylesSort;
  initialLimit?: FetchLifestylesLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
  source?: FetchCacheItemsSources;
  trackTotalHits?: FetchCacheItemsTrackTotalHits;
}

type CacheLifestylesOptions = CacheLifestylesDefaultOptions &
  (
    | CacheLifestylesWithPrefetchItemOptions
    | CacheLifestylesWithoutPrefetchItemOptions
  );

const scope = 'lifestyles';

function useCacheLifestyles<
  LifestyleItemType extends ReadInfiniteIndexQueryBaseNodeType
>({
  cacheKey,
  cacheQuery,
  defaultApi = 'read_api',
  fetchCacheItemQuery,
  fetchCacheItemSource = FetchCacheItemSources.ES,
  fetchItemCacheKey,
  fetchItemQuery,
  initialFilters = INITIAL_LIFESTYLES_FILTERS,
  initialLimit = INITIAL_LIFESTYLES_LIMIT,
  initialSort = INITIAL_LIFESTYLES_SORT,
  options = {},
  query,
  source = FetchCacheItemsSources.ES,
  trackTotalHits = false
}: CacheLifestylesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    prefetchItem,
    limitItems
  } = useCacheInfiniteIndexQuery<LifestyleItemType>({
    action: LifestylesPermissions.READ_LIFESTYLES_INDEX_ES_QUERY,
    cacheKey,
    cacheQuery,
    defaultApi,
    fetchCacheItemQuery,
    fetchCacheItemSource,
    fetchItemCacheKey,
    fetchItemQuery,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    query,
    scope,
    source,
    trackTotalHits
  });

  return {
    lifestylesData: data,
    lifestyles: items,
    lifestylesError: itemsError,
    lifestylesTotalCount: itemsTotalCount,
    lifestylesFetched: isFetched,
    lifestylesLoading: isLoading,
    lifestylesFetchingNextPage: isFetchingNextPage,
    lifestylesIsPlaceholderData: isPlaceholderData,
    lifestylesFilters: currentFilters,
    lifestylesSort: currentSort,
    lifestylesPage: currentPage,
    lifestylesLimit: currentLimit,
    hasNextLifestylesPage: hasNextPage,
    updateLifestyleCache: updateItemCache,
    loadMoreLifestyles: loadMoreItems,
    filterLifestyles: filterItems,
    changeLifestylesFilters: changeItemsFilters,
    clearLifestylesFilters: clearItemsFilters,
    clearLifestylesFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortLifestyles: sortItems,
    limitLifestyles: limitItems,
    prefetchLifestyle: useCallback(
      (lifestyleNanoId: LifestyleNanoID) => {
        const nanoId = lifestyleNanoId as unknown as UUID;
        prefetchItem(nanoId);
      },
      [prefetchItem]
    )
  };
}

export default useCacheLifestyles;
